import React, { ReactElement, useMemo, useRef, useState } from "react";
import {
    GroupBase,
    InputProps,
    OptionProps,
    SingleValue,
    components,
} from "react-select";
import AsyncSelect from "react-select/async";
import Select from "react-select/dist/declarations/src/Select";

import { isDefined } from "@utils/helpers";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import { CurrentLocationLink } from "@components/CurrentLocationLink/currentLocationLink.component";
import { Button } from "@components/General/Button/button.component";

import { SearchItemComponent } from "./components/searchItem/searchItem.component";
import { INPUT_STYLES } from "./storeSearchInput.config";
import { SetOptionsCallback } from "./storeSearchInput.container";
import { SearchItem } from "./storeSearchInput.types";

type StoreSearchInputComponentProps = {
    isLoading: boolean;
    value?: SearchItem;
    placeholderText: string;
    loadingText: string;
    noResultsText: string;
    buttonContent: ReactElement;
    menuPortalTarget: HTMLElement | null;
    clearable: boolean;
    "aria-label": string;
    loadOptions: (inputValue: string, callback: SetOptionsCallback) => void;
    onUseLocation: (latitude: number, longitude: number) => void;
    onLocationError: (errorMessage: string) => void;
    onSelect: (newValue: SingleValue<SearchItem>) => void;
    onClearValue?: () => void;
};

const StoreSearchInputComponent = ({
    isLoading,
    value,
    placeholderText,
    loadingText,
    noResultsText,
    buttonContent,
    menuPortalTarget,
    clearable,
    "aria-label": ariaLabel,
    loadOptions,
    onUseLocation,
    onLocationError,
    onSelect,
    onClearValue,
}: StoreSearchInputComponentProps) => {
    const { t } = useGranite();

    const [isOpen, setIsOpen] = useState<boolean>(false);

    const [isFocussed, setIsFocussed] = useState<boolean>(false);

    const focusRef = useRef<SearchItem>();
    const asyncRef =
        React.createRef<Select<SearchItem, false, GroupBase<SearchItem>>>();

    const Option = (props: OptionProps<SearchItem>) => {
        const { isFocused, data } = props;
        if (isFocused) focusRef.current = data;
        return <components.Option {...props} />;
    };

    const Input = useMemo(
        () => (props: InputProps<SearchItem>) =>
            (
                <components.Input
                    {...props}
                    inputClassName={"store-search-input__input"}
                />
            ),
        [],
    );

    const handleOnChange = (newValue: SingleValue<SearchItem>) => {
        onSelect(newValue);
        setIsFocussed(false);

        focusRef.current = undefined;
    };

    const handleOnInputChange = (newValue: string) => setIsOpen(!!newValue);

    const handleOnInputFocus = () => {
        if (asyncRef.current) {
            asyncRef.current.clearValue();
        }

        onClearValue?.();
        setIsFocussed(true);
    };

    const handleOnInputBlur = () => {
        setIsOpen(false);
        setIsFocussed(false);
    };

    const handleOnSearchClick = () => {
        if (!isDefined(focusRef.current)) return;

        onSelect(focusRef.current);
    };

    return (
        <>
            <div className={"store-search-input"}>
                <AsyncSelect
                    ref={asyncRef}
                    menuPortalTarget={menuPortalTarget}
                    menuIsOpen={isOpen}
                    onInputChange={handleOnInputChange}
                    isClearable={clearable}
                    cacheOptions={false}
                    isLoading={isLoading}
                    styles={INPUT_STYLES}
                    className={"store-search-input__select col p-0"}
                    components={{
                        Option,
                        Input,
                        LoadingIndicator: () => null,
                        DropdownIndicator: null,
                        IndicatorSeparator: null,
                    }}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    defaultOptions={false}
                    loadOptions={loadOptions}
                    isMulti={false}
                    onChange={handleOnChange}
                    onFocus={handleOnInputFocus}
                    onBlur={handleOnInputBlur}
                    blurInputOnSelect={true}
                    value={isFocussed ? undefined : value}
                    defaultValue={value}
                    formatOptionLabel={data => (
                        <SearchItemComponent
                            value={data.value}
                            label={data.highlightedLabel ?? data.label}
                            type={data.type}
                        />
                    )}
                    placeholder={placeholderText}
                    loadingMessage={() => loadingText}
                    noOptionsMessage={() => noResultsText}
                    aria-label={ariaLabel}
                />
                <Button
                    test="searchStore-button-submit"
                    color="primary"
                    type="button"
                    onClick={handleOnSearchClick}
                    aria-label={t("form.searchstore.submit")}
                    className={"store-search-input__submit"}
                >
                    {buttonContent}
                </Button>
            </div>
            <CurrentLocationLink
                className={"store-search-input__location"}
                label={t("form.searchstore.useCurrentLocation")}
                onCurrentLocationError={onLocationError}
                onCurrentLocationSuccess={onUseLocation}
            />
        </>
    );
};

export { StoreSearchInputComponent };
